import { useState, useEffect } from 'react';
import {
  Shield,
  GitBranch,
  FileText,
  ClipboardList,
} from 'lucide-react';
import { LoadingState } from '@systemdox/ui';
import { docsApi } from '@/services/docsApi';

interface Stats {
  installations: number;
  documents: number;
  specs: number;
  repos: number;
}

interface Installation {
  tenant: string;
  installationId: number;
  accountLogin: string;
  accountType: string;
  connectedAt: string;
}

export default function AdminDashboard() {
  const [stats, setStats] = useState<Stats | null>(null);
  const [installations, setInstallations] = useState<Installation[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    Promise.all([
      docsApi.getAdminStats(),
      docsApi.getAdminInstallations(),
    ]).then(([s, i]) => {
      setStats(s);
      setInstallations(i);
    }).catch((e) => {
      setError(e?.response?.status === 403 ? 'Admin access required' : 'Failed to load');
    }).finally(() => setLoading(false));
  }, []);

  if (loading) {
    return <LoadingState size="lg" />;
  }

  if (error) {
    return (
      <div className="p-8">
        <div className="p-4 rounded-lg bg-red-50 dark:bg-red-900/20 text-red-700 dark:text-red-300">
          <Shield className="w-5 h-5 inline mr-2" />{error}
        </div>
      </div>
    );
  }

  return (
    <div className="p-8">
      <div className="flex items-center gap-2 mb-6">
        <Shield className="w-5 h-5 text-slate-500" />
        <h1 className="text-2xl font-bold text-slate-900 dark:text-slate-100">Admin</h1>
      </div>

      {/* Stats */}
      {stats && (
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
          <div className="p-4 rounded-lg border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900">
            <GitBranch className="w-4 h-4 text-emerald-500 mb-2" />
            <p className="text-2xl font-bold text-slate-900 dark:text-slate-100">{stats.installations}</p>
            <p className="text-xs text-slate-500">Installations</p>
          </div>
          <div className="p-4 rounded-lg border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900">
            <GitBranch className="w-4 h-4 text-blue-500 mb-2" />
            <p className="text-2xl font-bold text-slate-900 dark:text-slate-100">{stats.repos}</p>
            <p className="text-xs text-slate-500">Connected Repos</p>
          </div>
          <div className="p-4 rounded-lg border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900">
            <FileText className="w-4 h-4 text-purple-500 mb-2" />
            <p className="text-2xl font-bold text-slate-900 dark:text-slate-100">{stats.documents}</p>
            <p className="text-xs text-slate-500">Documents</p>
          </div>
          <div className="p-4 rounded-lg border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900">
            <ClipboardList className="w-4 h-4 text-amber-500 mb-2" />
            <p className="text-2xl font-bold text-slate-900 dark:text-slate-100">{stats.specs}</p>
            <p className="text-xs text-slate-500">Specs</p>
          </div>
        </div>
      )}

      {/* Installations */}
      <h2 className="text-lg font-semibold text-slate-900 dark:text-slate-100 mb-3">GitHub App Installations</h2>
      {installations.length === 0 ? (
        <p className="text-sm text-slate-500">No installations</p>
      ) : (
        <div className="space-y-2">
          {installations.map((i) => (
            <div key={i.installationId} className="flex items-center justify-between p-3 rounded-lg border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900">
              <div>
                <p className="text-sm font-medium text-slate-900 dark:text-slate-100">{i.accountLogin}</p>
                <p className="text-xs text-slate-500">Tenant: {i.tenant} &middot; {i.accountType} &middot; ID: {i.installationId}</p>
              </div>
              <span className="text-xs text-slate-400">{new Date(i.connectedAt).toLocaleDateString()}</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
